import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiKey, FiUserPlus, FiSend, FiTrash2 } from "react-icons/fi";
import { getEmployees, filterEmployees, setEmployeeRoles, ensureEmployeeRole } from "../../services/employeeApi";
import { listAccounts, createAccount, setAccountActive, resetAccountPassword, generateTemporaryPassword } from "../../services/accountApi";
import useAsync from "../../hooks/useAsync";
import { useRole } from "../../context/RoleContext";
import {
  Card, PageHeader, DataTable, PersonCell, StatusBadge, SearchInput, ConfirmDialog, ErrorState, Button, Modal, FormField, Banner, OrgFilters,
} from "../../components/common";
import useOrgFilters from "../../hooks/useOrgFilters"; // [NEW] shared organisation filters
import { ROLE_LABELS } from "../../services/navigation";
import { getErrorMessage } from "../../utils/errors";
import { passwordPolicy } from "../../services/authApi";
import RolePermissionsCard from "./components/RolePermissionsCard";
import RoleCheckboxes, { roleListLabel } from "./components/RoleCheckboxes";
import InviteLoginModal from "../../components/invitations/InviteLoginModal";
import InvitationsTable from "../../components/invitations/InvitationsTable";
import { listInvitations } from "../../services/invitationApi";
import BulkInviteModal from "../../components/invitations/BulkInviteModal"; // [NEW] select-all / bulk invite
import BulkSelectionBar from "../../components/invitations/BulkSelectionBar";
import DeleteEmployeeDialog from "../../components/employees/DeleteEmployeeDialog"; // [NEW] delete employee
import { canDeleteEmployee } from "../../services/employeeLifecycleApi";

const api = { getEmployees, setEmployeeRoles, ensureEmployeeRole, listAccounts, createAccount, setAccountActive, resetAccountPassword };

const ACCESS = [
  // Columns follow ROLE_LABELS: Employee, Manager, HR, Management, Admin, IT Admin ([NEW] PM-5)
  ["Own goals, KRA/KPI, self assessment and results", true, true, true, true, true, true],
  ["Approve goals and assess direct reports", false, true, "If they have reports", "If they have reports", "If they have reports", "If they have reports"],
  ["All employee records, cycles, libraries and finalization", false, false, true, false, true, false],
  ["Organization dashboards and reports", false, false, true, "Read-only", true, false],
  ["User roles and application settings", false, false, false, false, true, "Users and audit trail only"],
];

const cell = (v) => (v === true ? "Yes" : v === false ? "No" : v);
const suggestUsername = (row) => (row.email ? row.email.split("@")[0] : row.name.toLowerCase().replace(/[^a-z0-9]+/g, "."))
  .toLowerCase().replace(/[^a-z0-9_.@-]/g, "").slice(0, 100);

export default function UserRolesPage() {
  const { profile } = useRole();
  const [search, setSearch] = useState("");
  const [version, setVersion] = useState(0);
  const { data, loading, error, reload } = useAsync(async () => {
    const [people, accounts] = await Promise.all([api.getEmployees({}, { fresh: version > 0 }), api.listAccounts()]);
    return { people: people.data, accounts: accounts.data };
  }, [version]);
  const refresh = () => setVersion((v) => v + 1);
  const [pending, setPending] = useState(null);
  const [account, setAccount] = useState(null);
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inviting, setInviting] = useState(null);
  const [invitationsVersion, setInvitationsVersion] = useState(0);
  // [NEW] Invitation list shared with the "Login invitations" card, so the failed count is always current.
  const invitations = useAsync(() => listInvitations(), [invitationsVersion]);
  const [invitationsFilter, setInvitationsFilter] = useState("all");
  const failedInvites = (invitations.data || []).filter((i) => i.status === "DELIVERY_FAILED").length;
  const showFailedInvites = () => { setInvitationsFilter("failed"); document.getElementById("login-invitations")?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const [selected, setSelected] = useState(() => new Set()); // [NEW] ticked employee IDs
  const [bulkOpen, setBulkOpen] = useState(false);
  const [deleting, setDeleting] = useState(null); // [NEW] employee row being deleted
  const permissions = useAsync(async () => ({ remove: await canDeleteEmployee() }), []);

  const org = useOrgFilters({ list: data?.people ?? null }); // [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity
  const rows = useMemo(() => {
    const byId = Object.fromEntries((data?.accounts || []).map((a) => [a.employeeId, a]));
    return filterEmployees(data?.people || [], { search }).filter((p) => org.matches(p)).map((p) => ({ ...p, account: byId[p.id] || null }));
  }, [data, search, org.matches]); // eslint-disable-line react-hooks/exhaustive-deps

  // [NEW] Only active employees without a login can be invited. Selection follows the filters, so hidden rows are never invited.
  const canInviteRow = (r) => !r.account && r.status === "Active" && r.id !== profile.id;
  const selectedRows = useMemo(() => rows.filter((r) => canInviteRow(r) && selected.has(r.id)), [rows, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    setBusy(true);
    try {
      if (pending.kind === "role") {
        if (pending.row.directReportCount > 0 && !pending.value.includes("manager") && pending.row.roles.includes("manager")) {
          throw new Error(`${pending.row.name} still has ${pending.row.directReportCount} direct report(s). Move them to another manager before removing the Manager role.`);
        }
        await api.setEmployeeRoles(pending.row.id, pending.value, { reason: pending.reason?.trim() || undefined });
        toast.success(`${pending.row.name}'s roles are now: ${roleListLabel(pending.value)}. It applies on their next request.`);
      } else {
        await api.setAccountActive(pending.row.id, pending.value === "Active");
        toast.success(`${pending.row.name} can ${pending.value === "Active" ? "now" : "no longer"} sign in.`);
      }
      setPending(null);
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const saveAccount = async () => {
    const rules = passwordPolicy(account.password);
    if (!rules.length || !rules.lowercase || !rules.uppercase || !rules.digit || !rules.special || !rules.noSpaces) { toast.error("Use 8 to 128 characters with uppercase, lowercase, a digit, and a special character; spaces are not allowed."); return; }
    setBusy(true);
    try {
      if (account.mode === "create") {
        await api.createAccount({ employeeId: account.row.id, username: account.username, password: account.password });
        await api.ensureEmployeeRole(account.row.id);
      } else {
        await api.resetAccountPassword(account.row.id, account.password);
      }
      setIssued({ name: account.row.name, username: account.username, password: account.password, mode: account.mode });
      setAccount(null);
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorState message={error} onRetry={reload} />;

  return (
    <div className="page">
      <PageHeader
        title="Users & roles"
        subtitle="Login accounts, application roles and permissions. The server enforces the same rules."
        actions={<SearchInput value={search} onChange={setSearch} placeholder="Search users" />}
      />
      <div className="filter-bar"><OrgFilters org={org} /></div>
      <BulkSelectionBar count={selectedRows.length} onClear={() => setSelected(new Set())} onInvite={() => setBulkOpen(true)} />
      <Card padded={false}>
        <DataTable
          loading={loading && !data}
          rows={rows}
          selection={{ selected, onChange: setSelected, isSelectable: canInviteRow, label: "employees without a login" }}
          columns={[
            { key: "name", label: "User", render: (r) => <PersonCell name={r.name} meta={r.email} /> },
            { key: "login", label: "Login", render: (r) => (r.account
              ? <span className="cell-stack"><strong>{r.account.username}</strong><small>{r.account.mustChangePassword ? "Temporary password" : r.account.lockedUntil ? "Locked after failed attempts" : "Password set"}</small></span>
              : <span className="muted">No login yet</span>) },
            { key: "dept", label: "Department", render: (r) => r.departmentName },
            { key: "reports", label: "Direct reports", align: "middle", render: (r) => r.directReportCount },
            {
              key: "role", label: "Roles",
              render: (r) => (
                <span className="cell-stack">
                  <span className="role-tags">{(r.roles.length ? r.roles : ["employee"]).map((k) => <span key={k} className="tag">{ROLE_LABELS[k] || k}</span>)}</span>
                  {r.id === profile.id ? <small className="muted">You can't change your own roles</small>
                    : r.status !== "Active" ? <small className="muted">Inactive employee: roles can't be changed</small>
                      : <button type="button" className="link-btn" onClick={() => setPending({ kind: "role", row: r, value: r.roles.length ? r.roles : ["employee"], reason: "" })}>Edit roles</button>}
                </span>
              ),
            },
            {
              key: "status", label: "Login status",
              render: (r) => (r.account
                ? <StatusBadge status={r.account.active ? "Active" : "Inactive"} />
                : <span className="muted">No login</span>),
            },
            {
              key: "act", label: "Actions", 
              render: (r) => (
                <span className="row-actions">
                  {!r.account && r.status === "Active" && (
                    <>
                      <Button size="sm" variant="secondary" icon={FiSend} title="Email a link so they choose their own password" onClick={() => setInviting(r)}>Invite by email</Button>
                      <Button size="sm" variant="ghost" icon={FiUserPlus} onClick={() => setAccount({ mode: "create", row: r, username: suggestUsername(r), password: generateTemporaryPassword() })}>Create login</Button>
                    </>
                  )}
                  {r.account && r.id !== profile.id && (
                    <>
                      <Button size="sm" variant="ghost" icon={FiKey} onClick={() => setAccount({ mode: "reset", row: r, username: r.account.username, password: generateTemporaryPassword() })}>{r.account.lockedUntil && new Date(r.account.lockedUntil) > new Date() ? "Unlock (new password)" : "Reset password"}</Button>
                      <button type="button" className="link-btn" onClick={() => setPending({ kind: "status", row: r, value: r.account.active ? "Inactive" : "Active" })}>
                        {r.account.active ? "Block login" : "Allow login"}
                      </button>
                    </>
                  )}
                  {permissions.data?.remove && r.id !== profile.id && (
                    <Button size="sm" variant="ghost" icon={FiTrash2} aria-label={`Delete ${r.name}`} title="Permanently delete (only if they have no records)" onClick={() => setDeleting(r)} />
                  )}
                </span>
              ),
            },
          ]}
        />
      </Card>

      {issued && (
        <Banner tone="success" title={`Temporary password for ${issued.name}`} action={<Button size="sm" variant="ghost" onClick={() => setIssued(null)}>Done</Button>}>
          Username <code>{issued.username}</code>, temporary password <code>{issued.password}</code>. Share it privately. They must
          change it at their first sign-in{issued.mode === "reset" ? "; their open sessions were signed out" : ""}.
          {issued.mode === "create" && " They have the Employee role; use Edit roles in the Roles column to add more."}
        </Banner>
      )}

      {failedInvites > 0 && (
        <Banner
          tone="danger"
          title={`${failedInvites} login ${failedInvites === 1 ? "invitation email" : "invitation emails"} could not be delivered`}
          action={<Button size="sm" variant="secondary" onClick={showFailedInvites}>View failed</Button>}
        >
          Open the failed list to resend them, one by one or all at once.
        </Banner>
      )}
      <div id="login-invitations">
        <Card title="Login invitations" subtitle="Emailed password-setup links: delivery, expiry and acceptance. Resend when a link expired or the email failed." padded={false}>
          <InvitationsTable version={invitationsVersion} source={invitations} filter={invitationsFilter} onFilterChange={setInvitationsFilter} />
        </Card>
      </div>
      <BulkInviteModal
        employees={bulkOpen ? selectedRows : null}
        onClose={() => setBulkOpen(false)}
        onFinished={() => { setSelected(new Set()); setInvitationsVersion((v) => v + 1); refresh(); }}
      />
      <DeleteEmployeeDialog
        employee={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={(emp) => { setDeleting(null); setSelected((prev) => { const next = new Set(prev); next.delete(emp.id); return next; }); setInvitationsVersion((v) => v + 1); refresh(); }}
      />
      <InviteLoginModal employee={inviting} onClose={() => setInviting(null)} onSent={() => { setInvitationsVersion((v) => v + 1); refresh(); }} />

      <RolePermissionsCard />

      <Card title="What each role can access" padded={false}>
        <div className="table-wrap">
          <table className="data-table access-table">
            <thead>
              <tr><th>Access</th>{Object.values(ROLE_LABELS).map((r) => <th key={r}>{r}</th>)}</tr>
            </thead>
            <tbody>
              {ACCESS.map(([label, ...vals]) => (
                <tr key={label}>
                  <td>{label}</td>
                  {vals.map((v, i) => <td key={i} className={v === true ? "yes" : v === false ? "no" : "partial"}>{cell(v)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ConfirmDialog
        open={Boolean(pending)}
        title={pending?.kind === "role" ? `Roles for ${pending?.row.name}` : `${pending?.value === "Active" ? "Allow" : "Block"} login for ${pending?.row.name}?`}
        message={
          pending?.kind === "role"
            ? `Tick the roles ${pending?.row.name} should have. Only the roles you tick or untick change; the others stay as they are.`
            : pending?.value === "Active" ? "They will be able to sign in again." : "They won't be able to sign in, and open sessions end now."
        }
        confirmLabel="Apply change"
        tone={pending?.value === "Inactive" ? "danger" : "primary"}
        loading={busy}
        onConfirm={apply}
        onClose={() => setPending(null)}
      >
        {pending?.kind === "role" && (
          <div className="form-stack">
            <RoleCheckboxes
              value={pending.value}
              directReportCount={pending.row.directReportCount}
              onChange={(value) => setPending((p) => ({ ...p, value }))}
            />
            <FormField label="Reason (optional)" hint="Saved in the audit trail.">
              <input value={pending.reason} onChange={(e) => setPending((p) => ({ ...p, reason: e.target.value }))} />
            </FormField>
            <p className="form-hint">
              Before: {roleListLabel(pending.row.roles)}. After: {roleListLabel(pending.value)}.
            </p>
          </div>
        )}
      </ConfirmDialog>

      <Modal
        open={Boolean(account)}
        size="sm"
        title={account?.mode === "create" ? `Create login for ${account?.row.name}` : `Reset password for ${account?.row.name}`}
        subtitle="They must change the temporary password when they first sign in."
        onClose={busy ? undefined : () => setAccount(null)}
        footer={<><Button variant="ghost" onClick={() => setAccount(null)} disabled={busy}>Cancel</Button><Button onClick={saveAccount} loading={busy}>{account?.mode === "create" ? "Create login" : "Set temporary password"}</Button></>}
      >
        {account && (
          <div className="form-stack">
            <FormField label="Username" hint="Letters, digits, . _ @ - (stored in lower case)">
              <input value={account.username} disabled={account.mode === "reset"} onChange={(e) => setAccount({ ...account, username: e.target.value.toLowerCase() })} />
            </FormField>
            <FormField label="Temporary password" hint="8 to 128 characters; uppercase, lowercase, digit, special character; no spaces.">
              <input value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} />
            </FormField>
          </div>
        )}
      </Modal>
    </div>
  );
}
